import { createColumnHelper, RowSelectionState } from '@tanstack/react-table';
import Papa from 'papaparse';
import { MouseEvent, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Outlet, useNavigate } from 'react-router-dom';

import { Role } from '@ndvr/auth-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { CopyGuidDropdownMenuItem } from '@/components/CopyGuidComponents';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { EllipsisDropdownMenuTrigger } from '@/components/ui/dropdown-menu-triggers';
import { Page, PageContent, PageTopBar } from '@/components/ui/page-layout';
import { formatDate, formatDateTime, titleCase } from 'utils';
import { downloadFile } from 'utils/file-download';
import { useUserHasRole } from 'views/admin/dev-queries';
import { CustomTablePage, PageSpinner, StaffOrPersonName, Text, TextLink, Tooltip } from 'widgets';
import { Toast } from 'widgets';

import { RequestReason, RequestStatus } from '../../../cip/_openapi/portfolio-optimizer';
import { useInvestmentRequestData } from './investment-request-hooks';
import { InvestmentRequestFilterBar } from './InvestmentRequestFilterBar';
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from './InvestmentRequestMultiFilter';
import { investmentRequestStatusToIcon } from './typeHelper';

export const InvestmentRequestListPage = () => {
    return (
        <Page>
            <PageTopBar />
            <InvestmentRequestsTable />
            <Outlet />
        </Page>
    );
};

export interface InvestmentRequestTableEntry {
    portfolioGuid: string;
    accountName: string;
    createdAt: string;
    createdBy: string;
    createdByName: string;
    requestId: number;
    status: RequestStatus;
    description: string;
    reason: RequestReason;
    custodyId?: string;
    custodyType?: string;
}

const columnHelper = createColumnHelper<InvestmentRequestTableEntry>();
const columns = [
    columnHelper.display({
        id: 'select',
        header: ({ table }) => (
            <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                    checked={table.getIsAllPageRowsSelected()}
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            </div>
        ),
        cell: ({ row }) => {
            const isDisabled =
                row.original.status === RequestStatus.COMPLETED || row.original.status === RequestStatus.CANCELLED;
            return (
                <div onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={row.getIsSelected()}
                        onCheckedChange={(value) => row.toggleSelected(!!value)}
                        aria-label="Select row"
                        disabled={isDisabled}
                    />
                </div>
            );
        },
        size: 40,
        enableSorting: false,
    }),
    columnHelper.accessor('createdAt', {
        header: 'Created At',
        cell: ({ getValue }) => <Tooltip tooltip={formatDateTime(getValue())}>{formatDate(getValue())}</Tooltip>,
        sortingFn: 'datetime',
        size: 120,
    }),
    columnHelper.accessor('status', {
        header: 'Status',
        size: 60,
        cell: ({ getValue }) => (
            <Tooltip tooltip={titleCase(getValue())}>{investmentRequestStatusToIcon(getValue())}</Tooltip>
        ),
    }),
    columnHelper.accessor('custodyId', {
        header: 'Brokerage',
        size: 120,
        cell: ({ row, getValue }) => (
            <div>
                <div>
                    <CopiableGuid
                        guid={getValue() || ''}
                        showDigits="lastFour"
                        showBorder={false}
                        shortenedPrefix="x"
                    />
                </div>
                <div>{titleCase(row.original.custodyType)}</div>
            </div>
        ),
    }),
    columnHelper.accessor('accountName', {
        header: 'Account',
        cell: ({ row }) => {
            const accountName = row.original.accountName;
            return accountName ? (
                <div className="[&_*]:overflow-ellipsis">
                    <Tooltip tooltip={accountName} wrapperClassName="overflow-hidden">
                        <TextLink
                            to={`/account/${row.original.portfolioGuid}`}
                            onClick={(e: MouseEvent) => e.stopPropagation()}
                        >
                            {accountName}
                        </TextLink>
                    </Tooltip>
                </div>
            ) : (
                'System'
            );
        },
        size: 200,
    }),
    columnHelper.accessor('portfolioGuid', {
        header: 'Portfolio GUID',
        cell: ({ getValue }) => (
            <CopiableGuid guid={getValue()} showDigits="lastFour" showBorder={false} shortenedPrefix="x" />
        ),
        size: 150,
    }),
    columnHelper.accessor('createdByName', {
        header: 'Created By',
        size: 150,
        cell: ({ row }) => (row.original.createdBy ? <StaffOrPersonName guid={row.original.createdBy} /> : 'System'),
    }),
    columnHelper.accessor('description', {
        header: 'Description',
        cell: ({ getValue }) => (
            <div className="[&_*]:overflow-ellipsis">
                <Tooltip tooltip={getValue()} wrapperClassName="overflow-hidden">
                    <Text>{getValue()}</Text>
                </Tooltip>
            </div>
        ),
    }),
    columnHelper.accessor('reason', {
        header: 'Reason',
        cell: ({ getValue }) => titleCase(getValue()),
        size: 120,
    }),
    columnHelper.accessor('requestId', {
        header: 'Request Id',
        size: 100,
        cell: ({ getValue }) => <div style={{ textAlign: 'left' }}>{getValue()}</div>,
    }),
    columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
            <DropdownMenu>
                <EllipsisDropdownMenuTrigger />
                <DropdownMenuContent>
                    <CopyGuidDropdownMenuItem guid={row.original.portfolioGuid} />
                </DropdownMenuContent>
            </DropdownMenu>
        ),
        size: 50,
    }),
];

const getDefaultValues = () => {
    const savedStates = localStorage.getItem('investmentRequestStates');
    const investmentRequestStates = savedStates ? JSON.parse(savedStates) : [];
    return {
        investmentRequestStates,
        startDate: DEFAULT_START_DATE,
        endDate: DEFAULT_END_DATE,
    };
};

const InvestmentRequestsTable = () => {
    const formMethods = useForm({ defaultValues: getDefaultValues() });
    const [startDate, endDate, investmentRequestStates] = formMethods.watch([
        'startDate',
        'endDate',
        'investmentRequestStates',
    ]);
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const iopsPortalRole = useUserHasRole(Role.IOPS_PORTAL_ACCESS);
    const hasCustodyAccess = !!iopsPortalRole.data;
    const isRoleCheckPending = iopsPortalRole.isPending;

    const {
        investmentRequests,
        isLoading: isInvestmentRequestsLoading,
        isUpdating: isInvestmentRequestsUpdating,
        loadingStates: { isLoadingTenantAccounts: isTenantAccountsLoading },
    } = useInvestmentRequestData({ filterByTenant: !hasCustodyAccess });

    const filtered: InvestmentRequestTableEntry[] = useMemo(() => {
        return (investmentRequests || []).filter((request: InvestmentRequestTableEntry) => {
            if (investmentRequestStates?.length > 0 && !investmentRequestStates.includes(request.status)) return false;
            if (request.status === RequestStatus.IN_PROGRESS) return true;
            if (!request.createdAt) return true;
            try {
                const createdAt = new Date(request.createdAt);
                return (!startDate || createdAt >= new Date(startDate)) && (!endDate || createdAt <= new Date(endDate));
            } catch (error) {
                Toast.forError(error, 'Error with date filtering');
                return true;
            }
        });
    }, [investmentRequests, investmentRequestStates, startDate, endDate]);

    if (isInvestmentRequestsLoading || isRoleCheckPending || (!hasCustodyAccess && isTenantAccountsLoading)) {
        return <PageSpinner />;
    }

    const selectedRequestIds = Object.keys(rowSelection).filter((id) => rowSelection[id]);
    const allPortfolioGuids = selectedRequestIds
        .map((requestIdStr) => {
            const requestId = parseInt(requestIdStr);
            const request = filtered.find((r) => r.requestId === requestId);
            return request?.portfolioGuid;
        })
        .filter((guid): guid is string => guid !== undefined);
    const selectedPortfolioGuids = Array.from(new Set(allPortfolioGuids));
    const selectedCount = selectedPortfolioGuids.length;
    const selectedRequestCount = selectedRequestIds.length;

    const handleExportCsv = () => {
        const csv = Papa.unparse(filtered);
        downloadFile(
            `investment_requests_export_${formatDate(new Date(), 'SERVER')}.csv`,
            window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' })),
        );
    };

    return (
        <FormProvider {...formMethods}>
            <InvestmentRequestFilterBar
                count={filtered.length}
                onExportCsv={handleExportCsv}
                selectedRequestCount={selectedRequestCount}
                selectedPortfolioCount={selectedCount}
                selectedPortfolioGuids={selectedPortfolioGuids}
            />
            <PageContent scroll="children" padding="none">
                <InvestmentRequestTableView
                    data={filtered}
                    isUpdating={isInvestmentRequestsUpdating}
                    rowSelection={rowSelection}
                    setRowSelection={setRowSelection}
                />
            </PageContent>
        </FormProvider>
    );
};

const InvestmentRequestTableView = ({
    data,
    isUpdating,
    rowSelection,
    setRowSelection,
}: {
    data: InvestmentRequestTableEntry[];
    isUpdating: boolean;
    rowSelection: RowSelectionState;
    setRowSelection: (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => void;
}) => {
    const navigate = useNavigate();

    return (
        <CustomTablePage
            data={data}
            columns={columns}
            exportName="investmentRequests"
            rowOnClick={(entry) => navigate(`/trading/investment-requests/${entry.requestId}`)}
            initialColumnVisibility={{
                householdName: false,
                clientName: false,
                portfolioGuid: false,
            }}
            cardClassName="m-0 border-0 shadow-none"
            isUpdating={isUpdating}
            enableRowSelection={(row: InvestmentRequestTableEntry) =>
                row.status !== RequestStatus.COMPLETED && row.status !== RequestStatus.CANCELLED
            }
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            getRowId={(row: InvestmentRequestTableEntry) => row.requestId.toString()}
        />
    );
};
